import React, { useEffect, useState, useCallback, useReducer } from 'react';
import PropTypes from 'prop-types';
import QuestionActionListCard from './QuestionActionListCard';
import { useDebounce } from 'utils/customHooks';
import { initialState, reducer } from 'features/questionnaires/reducers/questionActions/containerReducer';
import { questionActionValidator } from 'features/questionnaires/validators';
import { useValidation } from '@totalsoft/pure-validations-react';
import { QUESTIONACTION_SAVE, QUESTIONACTION_DELETE, EVALUATE_QUESTION_ACTION_FORMULA } from 'features/questionnaires/mutations';
import { QUESTIONACTION_DELETE_SUBSCRIPTION } from 'features/questionnaires/subscriptions';
import { QUESTIONACTIONTYPES_QUERY } from 'features/questionnaires/queries';
import { useApolloClient } from '@apollo/client';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useToast } from 'hooks/toasts/useToast';
import { useTranslation } from 'react-i18next';

const rowsPerPageOptions = [5, 10, 20];
const defaultPagination = { page: 0, pageSize: 5 };

const QuestionActionsContainer = ({ questionnaireId, data, isReadOnly }) => {
  const client = useApolloClient();
  const { t } = useTranslation();
  const addToast = useToast();

  const [questionActionFilter, setQuestionActionFilter] = useState('');
  const [pagination, setPagination] = useState(defaultPagination);
  const [localState, dispatch] = useReducer(reducer, initialState);

  const [validation, validate] = useValidation(questionActionValidator);
  const debounceFilterValue = useDebounce(questionActionFilter, 250);
  const handleChangeFilterValue = filterValue => {
    setQuestionActionFilter(filterValue);
  };

  const { data: questionActionTypesData } = useQuery(QUESTIONACTIONTYPES_QUERY, {
    skip: !questionnaireId
  });

  const [QuestionActionSave] = useMutation(QUESTIONACTION_SAVE);

  const [EvaluateQuestionActionFormula] = useMutation(EVALUATE_QUESTION_ACTION_FORMULA);

  const [QuestionActionDelete] = useMutation(QUESTIONACTION_DELETE);

  useSubscription(QUESTIONACTION_DELETE_SUBSCRIPTION, {
    onData: ({ client }) => {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'questionnaire' });
      client.cache.gc();
      addToast(t('Questionnaire.QuestionAction.QuestionActionDeleted'), 'success');
    }
  });

  const getItemsPerPage = useCallback((questionActions, pageSize, page, filter) => {
    let result;
    if (filter !== '') {
      result = filterQuestionActionsByValue(questionActions, filter);
    } else {
      const current = page * pageSize;
      result = questionActions.slice(current, current + pageSize);
    }
    return result;
  }, []);

  const filterQuestionActionsByValue = (questionActions, filter) => {
    if (filter === '') return questionActions;
    return questionActions.filter(o =>
      Object.keys(o).some(k => {
        if (o[k] !== null && k === 'questionActionType')
          return o[k].name?.toString().toLowerCase().includes(filter.toString().toLowerCase());
        return null;
      })
    );
  };

  const handlePageChange = page => {
    setPagination({ ...pagination, page });
  };

  const handleRowsPerPageChange = size => {
    setPagination({ ...pagination, pageSize: size, page: defaultPagination.page });
  };

  const handlePaginationAndFiltersRefresh = () => {
    setPagination({ ...defaultPagination });
  };

  const handleAddNewQuestionAction = () => {
    dispatch({
      type: 'OnQuestionActionAdded',
      index: pagination.pageSize * pagination.page,
      questionnaireId
    });
  };

  const getIndexInFilteredList = index => {
    return pagination.pageSize * pagination.page + index;
  };

  const handleExpandedChanged = (index, isExpanded) => {
    dispatch({ type: 'OnQuestionActionExpanded', index: getIndexInFilteredList(index), isExpanded });
  };

  const handlePropertyChanged = (propName, propValue, index) => {
    dispatch({ type: 'onActionDetailsPropertyChanged', propName, propValue, index });
  };

  const handleSave = index => async () => {
    // eslint-disable-next-line no-unused-vars
    const { changed, isExpanded, questionActionTypes, ...questionAction } = localState.questionActions[index];
    const saveInput = {
      variables: {
        input: { ...questionAction }
      }
    };

    const evaluationResult = await EvaluateQuestionActionFormula(saveInput);
    if (evaluationResult.data?.evaluateQuestionActionFormula?.error) {
      addToast(t(evaluationResult.data?.evaluateQuestionActionFormula?.error), 'error');
    } else if (evaluationResult.data?.evaluateQuestionActionFormula?.isOk) {
      const result = await QuestionActionSave(saveInput);
      if (result.data.questionAction.isSaved) {
        client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'questionnaire' });
        client.cache.gc();
        dispatch({
          type: 'OnQuestionActionSaved',
          data: { id: result.data.questionAction.questionActionId, idx: index }
        });
      }
    } else {
      addToast(t('Questionnaire.QuestionActionFormulaNotOk'), 'error');
    }
  };

  const handleDelete = index => () => {
    if (window.confirm(t('Questionnaire.DeleteQuestionAction'))) {
      const { questionActionId } = localState.questionActions[index];
      if (questionActionId) QuestionActionDelete({ variables: { questionActionId } });
      else dispatch({ type: 'OnQuestionActionDeleted', index });
    }
  };

  useEffect(() => {
    if (data) {
      dispatch({ type: 'OnInitState', data });
    }
  }, [data]);

  useEffect(() => {
    validate(localState.questionActions);
  }, [localState.questionActions, validate]);

  return (
    <QuestionActionListCard
      questionActions={getItemsPerPage(localState.questionActions, pagination.pageSize, pagination.page, debounceFilterValue)}
      questionActionTypes={questionActionTypesData ? questionActionTypesData.questionActionTypes : []}
      onSave={handleSave}
      onDelete={handleDelete}
      validation={validation}
      isReadOnly={isReadOnly}
      pagination={pagination}
      totalCount={filterQuestionActionsByValue(localState.questionActions, debounceFilterValue).length}
      fullTextFilter={questionActionFilter}
      onChangeFilterValue={handleChangeFilterValue}
      onRowsPerPageChange={handleRowsPerPageChange}
      onPageChange={handlePageChange}
      onRefresh={handlePaginationAndFiltersRefresh}
      onAddNewQuestionAction={handleAddNewQuestionAction}
      rowsPerPageOptions={rowsPerPageOptions}
      onPropertyChanged={handlePropertyChanged}
      onExpandedChanged={handleExpandedChanged}
      getIndexInFilteredList={getIndexInFilteredList}
    />
  );
};

QuestionActionsContainer.propTypes = {
  questionnaireId: PropTypes.number.isRequired,
  data: PropTypes.array.isRequired,
  isReadOnly: PropTypes.bool
};

export default QuestionActionsContainer;
